import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { teamGroupKeys } from "@/hooks/queryKeys";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";

interface GroupMemberOption {
  user_id: string;
}

/**
 * useGroupMembers (TES-1867 / L6-1) — die Mitglieder einer Fachbereich-Gruppe, für den optionalen
 * Bearbeiter-Picker an Prüfung/Prüfpunkt. Liest die kanonische `group_effective_members`-Sicht (Mig 330;
 * statisch via group_members, dynamisch erweitert in Mig 334) über den user-JWT-scoped Client, damit RLS
 * die Sichtbarkeit gated. Der gewählte Bearbeiter MUSS Mitglied sein (DB-Guard `validate_assignee_group_membership`,
 * Mig 358) — der Picker bietet daher nur Mitglieder an.
 */
export function useGroupMembers(groupId: string | null | undefined, enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: teamGroupKeys.members(groupId),
    queryFn: async (): Promise<GroupMemberOption[]> => {
      /* istanbul ignore next -- defensiver Guard; `enabled` verhindert queryFn ohne groupId */
      if (!groupId) return [];
      const { data, error } = await supabase
        .from("group_effective_members")
        .select("user_id")
        .eq("group_id", groupId);
      if (error) throw error;
      // group_effective_members.user_id is nullable in the generated types (view) — drop NULLs so the
      // assignee picker only offers real members (the DB guard requires a real member anyway, Mig 358).
      return (data ?? []).filter((r): r is { user_id: string } => r.user_id !== null);
    },
    enabled: enabled && !!groupId,
    staleTime: 5 * 60 * 1000,
  });

  // TES-2027: group_members + group_members_effective sind bereits realtime-registriert +
  // publiziert (RealtimeContext / Mig 370). Bislang war der Bearbeiter-Picker reines Pull
  // (5 min staleTime) — Mitgliedschafts-Änderungen anderer User kamen nie an (Anti-Pattern
  // Variante 2). Push-Invalidierung schließt die Lücke ohne zusätzliche DB-Connection.
  useRealtimeSubscription(
    ["group_members", "group_members_effective"],
    () => queryClient.invalidateQueries({ queryKey: teamGroupKeys.members(groupId) }),
    { enabled: enabled && !!groupId },
  );

  return {
    members: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
